
import { Component, Prop, State, Vue, Watch } from 'nuxt-property-decorator';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInWeeks from 'date-fns/differenceInWeeks';
import differenceInMonths from 'date-fns/differenceInMonths';
import differenceInYears from 'date-fns/differenceInYears';
import { IGameItemForSaleByGala, IGamesState } from '~/store/games/types';
import { getAmountLeftInStore, getTotalSupply } from '~/utils/productSupply';
import { getDateFormatsFromLocale, SupportedLocales } from '~/utils/locales';
import CountdownTimer from '~/components/Promotions/CountdownTimer.vue';
import SupplyRemainingBadge from './SupplyRemainingBadge.vue';

type TSaleItemQuantities = Pick<IGameItemForSaleByGala, 'qtyLeft' | 'rarity'>;

@Component({
  components: {
    CountdownTimer,
    SupplyRemainingBadge,
  },
})
export default class ProjectSaleItemsProgress extends Vue {
  @Prop(String) saleName!: string;
  @Prop(String) saleStartsAt!: string;
  @Prop(String) saleEndsAt!: string;
  @Prop(Number) progressBarDisplayThreshhold!: number;

  @State((state: IGamesState) => state.itemsForSaleByGala, {
    namespace: 'games',
  })
  itemsForSale!: IGamesState['itemsForSaleByGala'];
  @State((state: IGamesState) => state.itemsForSaleState, {
    namespace: 'games',
  })
  itemsForSaleState!: IGamesState['itemsForSaleState'];

  interval: ReturnType<typeof setInterval> | null = null;
  intervalDuration = 0;
  compareTime = 0;
  saleEndTime = 0;

  beforeDestroy() {
    this.interval && clearInterval(this.interval);
  }

  get showPercentInfo() {
    if (!this.progressBarDisplayThreshhold) {
      return true;
    }
    return (
      this.fractionSold &&
      this.fractionSold * 100 >= this.progressBarDisplayThreshhold
    );
  }

  get fractionSold() {
    return !this.combinedItemsForSale?.rarity?.supplyLimit
      ? undefined
      : 1 -
          this.combinedItemsForSale.qtyLeft /
            this.combinedItemsForSale.rarity.supplyLimit;
  }

  get isCompletelySoldOut() {
    return this.combinedItemsForSale.qtyLeft === 0;
  }

  get percentSold() {
    return this.$options?.filters?.formatNumber(this.fractionSold, '0%');
  }

  getIntervalDuration(timestamp1: number, timestamp2: number) {
    const searchItems = [
      { interval: 0, diff: differenceInSeconds },
      { interval: 1000, diff: differenceInMinutes },
      { interval: 1000 * 60, diff: differenceInHours },
      { interval: 1000 * 60 * 60, diff: differenceInDays },
      { interval: 1000 * 60 * 60 * 24, diff: differenceInWeeks },
      { interval: 1000 * 60 * 60 * 24 * 7, diff: differenceInMonths },
      { interval: 1000 * 60 * 60 * 24 * 30, diff: differenceInYears },
    ];
    const max = 1000 * 60 * 60 * 24 * 365;
    const item = searchItems.find((item) => !item.diff(timestamp1, timestamp2));
    return item ? item.interval : max;
  }

  setTimer() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    this.compareTime = Date.now();
    this.intervalDuration = this.getIntervalDuration(
      this.saleEndTime,
      this.compareTime,
    );
    if (!this.intervalDuration) {
      return;
    }

    const intervalFunc = () => {
      const now = Date.now();
      if (now < this.saleEndTime) {
        this.compareTime = Date.now();
        if (this.intervalDuration > 1000) {
          const newDuration = this.getIntervalDuration(
            this.saleEndTime,
            this.compareTime,
          );
          if (newDuration && newDuration !== this.intervalDuration) {
            this.interval && clearInterval(this.interval);
            this.interval = setInterval(intervalFunc, newDuration);
            this.intervalDuration = newDuration;
          }
        }
      } else {
        this.compareTime = this.saleEndTime;
        this.interval && clearInterval(this.interval);
      }
    };

    this.interval = setInterval(intervalFunc, this.intervalDuration);
  }

  @Watch('saleEndsAt', { immediate: true })
  handleSaleEndChange(current: string | undefined) {
    if (current && !this.isSaleOver) {
      this.saleEndTime = new Date(this.saleEndsAt).getTime();
      this.setTimer();
    }
  }

  get isSaleOver() {
    if (!this.saleEndsAt) {
      return false;
    }

    const endDate = new Date(this.saleEndsAt);
    const now = new Date();
    return !(now < endDate);
  }

  get timeRemaining() {
    if (!this.saleEndsAt || !this.compareTime) {
      return;
    }
    return formatDistanceStrict(this.saleEndTime, this.compareTime, {
      locale: getDateFormatsFromLocale(this.$i18n.locale as SupportedLocales),
    });
  }

  get combinedItemsForSale() {
    const initial: TSaleItemQuantities = {
      qtyLeft: 0,
      rarity: {
        icon: '',
        supplyLimit: 0,
      },
    };
    const combinedItem: TSaleItemQuantities = this.itemsForSale?.reduce(
      (combined, item) => {
        // we only provide qtyLeft and rarityData.supplyLimit for the combined items progress bar
        const qtyLeft = getAmountLeftInStore(item);
        const supplyLimit = getTotalSupply(item);
        return {
          qtyLeft: combined.qtyLeft + qtyLeft,
          rarity: {
            icon: '',
            supplyLimit: (combined.rarity?.supplyLimit || 0) + supplyLimit,
          },
        };
      },
      initial,
    );

    return combinedItem;
  }
}
